import React, { useMemo, useState } from 'react';
import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import FlightService from '../../../services/FlightService';

type RunItRealTimeJobSettingsProps = {
  historySelected: boolean;
  options: { skipInventory?: boolean; loadFullInventory?: boolean };
  onChange: (next: { skipInventory?: boolean; loadFullInventory?: boolean }) => void;
  client: any;
  tipContext?: any;
};

type AdvancedSettingsState = {
  open: boolean;
  loading: boolean;
  saving: boolean;
  error: string;
  autoMergeActuals: boolean;
  runsqlId: string;
  runsqlPw: string;
  endpointUrl: string;
};

type AdvancedRunState = {
  open: boolean;
  running: boolean;
  error: string;
  period: Moment;
  steps: {
    products: boolean;
    inventory: boolean;
    orders: boolean;
    transfers: boolean;
    purchaseOrders: boolean;
  };
  merge: {
    actuals: boolean;
    onOrder: boolean;
  };
};

const defaultAdvancedSettingsState = (): AdvancedSettingsState => ({
  open: false,
  loading: false,
  saving: false,
  error: '',
  autoMergeActuals: false,
  runsqlId: '',
  runsqlPw: '',
  endpointUrl: '',
});

const defaultAdvancedRunState = (): AdvancedRunState => ({
  open: false,
  running: false,
  error: '',
  period: moment(),
  steps: {
    products: true,
    inventory: true,
    orders: true,
    transfers: true,
    purchaseOrders: true,
  },
  merge: {
    actuals: false,
    onOrder: false,
  },
});

const RunItRealTimeJobSettings: React.FC<RunItRealTimeJobSettingsProps> = ({
  historySelected,
  options,
  onChange,
  client,
  tipContext,
}) => {
  const [advancedSettings, setAdvancedSettings] = useState<AdvancedSettingsState>(defaultAdvancedSettingsState());
  const [advancedRun, setAdvancedRun] = useState<AdvancedRunState>(defaultAdvancedRunState());
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeRunning, setMergeRunning] = useState(false);
  const [mergeError, setMergeError] = useState('');
  const [mergeModeHistory, setMergeModeHistory] = useState(false);
  const [mergeStartMonth, setMergeStartMonth] = useState<Moment>(moment().subtract(1, 'month').startOf('month'));
  const [mergeEndMonth, setMergeEndMonth] = useState<Moment>(moment().subtract(1, 'month').startOf('month'));

  const isCurrentMonth = useMemo(() => advancedRun.period.isSame(moment(), 'month'), [advancedRun.period]);

  const patchAdvancedSettings = (next: Partial<AdvancedSettingsState>) => {
    setAdvancedSettings((prev) => ({ ...prev, ...next }));
  };

  const patchAdvancedRun = (next: Partial<AdvancedRunState>) => {
    setAdvancedRun((prev) => ({ ...prev, ...next }));
  };

  const patchRunSteps = (next: Partial<AdvancedRunState['steps']>) => {
    setAdvancedRun((prev) => ({ ...prev, steps: { ...prev.steps, ...next } }));
  };

  const patchRunMerge = (next: Partial<AdvancedRunState['merge']>) => {
    setAdvancedRun((prev) => ({ ...prev, merge: { ...prev.merge, ...next } }));
  };

  const openAdvancedSettings = async () => {
    if (!client?.Id || advancedSettings.loading) return;

    patchAdvancedSettings({ open: true, loading: true, error: '' });

    try {
      const res = await FlightService.getRunItRealTimeAdvancedSettings(client.Id);
      const data = res?.result ?? res;
      patchAdvancedSettings({
        autoMergeActuals: !!data?.auto_merge_actuals,
        runsqlId: data?.runit_runsql_id ?? '',
        runsqlPw: data?.runit_runsql_pw ?? '',
        endpointUrl: data?.runit_endpoint_url ?? '',
      });
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to load advanced settings';
      patchAdvancedSettings({ error: msg });
    } finally {
      patchAdvancedSettings({ loading: false });
    }
  };

  const onSaveAdvancedSettings = async () => {
    if (!client?.Id || advancedSettings.saving) return;

    patchAdvancedSettings({ error: '' });

    try {
      patchAdvancedSettings({ saving: true });
      await FlightService.updateRunItRealTimeAdvancedSettings(client.Id, {
        auto_merge_actuals: advancedSettings.autoMergeActuals,
        runit_runsql_id: advancedSettings.runsqlId.trim(),
        runit_runsql_pw: advancedSettings.runsqlPw,
        runit_endpoint_url: advancedSettings.endpointUrl.trim(),
      });
      tipContext?.showSuccess?.('Advanced settings saved.', 8);
      patchAdvancedSettings({ open: false });
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to save advanced settings';
      patchAdvancedSettings({ error: msg });
      tipContext?.show?.(msg, 12);
    } finally {
      patchAdvancedSettings({ saving: false });
    }
  };

  const onAdvancedRun = async () => {
    if (!client?.Id || advancedRun.running) return;

    patchAdvancedRun({ error: '' });

    const steps: string[] = [];
    if (advancedRun.steps.products) steps.push('products');
    if (advancedRun.steps.inventory) steps.push('inventory');
    if (advancedRun.steps.orders) steps.push('orders');
    if (advancedRun.steps.transfers) steps.push('transfers');
    if (advancedRun.steps.purchaseOrders) steps.push('purchase_orders');

    const mergeOptions = {
      actuals: advancedRun.merge.actuals && isCurrentMonth,
      onOrder: advancedRun.merge.onOrder && isCurrentMonth,
    };

    if (steps.length === 0 && !mergeOptions.actuals && !mergeOptions.onOrder) {
      patchAdvancedRun({ error: 'Please select at least one step or merge operation.' });
      return;
    }

    try {
      patchAdvancedRun({ running: true });
      tipContext?.showSuccess?.('Starting RunIt advanced run...', 8);

      await FlightService.runRunItRealTimeAdvanced(
        client.Id,
        advancedRun.period.format('YYYYMM'),
        steps,
        mergeOptions
      );

      tipContext?.showSuccess?.('RunIt advanced run completed.', 10);
      patchAdvancedRun({ open: false });
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'RunIt advanced run failed';
      patchAdvancedRun({ error: msg });
      tipContext?.show?.(msg, 12);
    } finally {
      patchAdvancedRun({ running: false });
    }
  };

  const onMergeActuals = async () => {
    if (!client?.Id || mergeRunning) return;

    setMergeError('');

    const range = mergeModeHistory
      ? {
          startDate: mergeStartMonth.clone().startOf('month').format('YYYY-MM-DD'),
          endDate: mergeEndMonth.clone().endOf('month').format('YYYY-MM-DD'),
        }
      : {};

    if (mergeModeHistory && mergeStartMonth.isAfter(mergeEndMonth, 'month')) {
      setMergeError('Start month must be on or before end month.');
      return;
    }

    try {
      setMergeRunning(true);
      tipContext?.showSuccess?.('Merging RunIt actuals. This may take several minutes...', 10);
      await FlightService.mergeRunItActuals(client.Id, range);
      tipContext?.showSuccess?.('RunIt actuals merged successfully.', 10);
      setMergeModalOpen(false);
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'RunIt actuals merge failed';
      setMergeError(msg);
      tipContext?.show?.(msg, 12);
    } finally {
      setMergeRunning(false);
    }
  };

  return (
    <>
      <FormControlLabel
        sx={{ ml: 1 }}
        control={
          <Checkbox
            checked={!!options.skipInventory}
            disabled={!!options.loadFullInventory}
            onChange={(e) => onChange({ ...options, skipInventory: e.target.checked })}
            color="primary"
          />
        }
        label="Skip inventory load?"
      />
      <FormControlLabel
        sx={{ ml: 1 }}
        control={
          <Checkbox
            checked={!!options.loadFullInventory}
            disabled={!!historySelected}
            onChange={(e) => {
              if (e.target.checked) {
                onChange({ ...options, loadFullInventory: true, skipInventory: false });
                return;
              }
              onChange({ ...options, loadFullInventory: false });
            }}
            color="primary"
          />
        }
        label="Load full inventory? (only for first time loads)"
      />

      <div className="mt-3 border-t border-gray-300 pt-3 px-1">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-50"
            onClick={() => { setMergeError(''); setMergeModalOpen(true); }}
            disabled={mergeRunning}
          >
            Merge Actuals
          </button>

          <button
            type="button"
            className="inline-flex items-center rounded-md border border-gray-400 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100"
            onClick={openAdvancedSettings}
            disabled={advancedSettings.loading || advancedSettings.saving}
          >
            Advanced Settings
          </button>

          <button
            type="button"
            className="inline-flex items-center rounded-md border border-blue-400 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-800 hover:bg-blue-100 disabled:opacity-50"
            onClick={() => patchAdvancedRun({ open: true, error: '' })}
            disabled={advancedRun.running}
          >
            Advanced Run
          </button>
        </div>
      </div>

      {mergeModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => (!mergeRunning ? setMergeModalOpen(false) : undefined)} />

          <div className="relative z-10 w-full max-w-lg rounded-lg bg-white p-4 shadow-lg">
            <div className="flex items-start justify-between gap-3">
              <div className="text-base font-semibold">Merge RunIt Actuals</div>
              <button
                type="button"
                className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                onClick={() => setMergeModalOpen(false)}
                disabled={mergeRunning}
              >
                Close
              </button>
            </div>

            <div className="mt-3 text-sm text-gray-800">
              Merge loaded RunIt data into actuals. This may take several minutes.
            </div>

            <div className="mt-4 flex w-fit overflow-hidden rounded-md border border-gray-300">
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium ${!mergeModeHistory ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'} disabled:opacity-50`}
                onClick={() => setMergeModeHistory(false)}
                disabled={mergeRunning}
              >
                Current Month
              </button>
              <button
                type="button"
                className={`border-l border-gray-300 px-4 py-2 text-sm font-medium ${mergeModeHistory ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'} disabled:opacity-50`}
                onClick={() => setMergeModeHistory(true)}
                disabled={mergeRunning}
              >
                History
              </button>
            </div>

            {mergeModeHistory ? (
              <div className="mt-4 grid grid-cols-2 gap-4">
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    views={['year', 'month']}
                    label="Start Month"
                    minDate={moment('2000-01-01')}
                    maxDate={moment().subtract(1, 'month')}
                    value={mergeStartMonth}
                    onChange={(value: Moment | null) => value && setMergeStartMonth(value)}
                    disabled={mergeRunning}
                    renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                    inputFormat="MMM YYYY"
                  />
                  <DatePicker
                    views={['year', 'month']}
                    label="End Month"
                    minDate={moment('2000-01-01')}
                    maxDate={moment().subtract(1, 'month')}
                    value={mergeEndMonth}
                    onChange={(value: Moment | null) => value && setMergeEndMonth(value)}
                    disabled={mergeRunning}
                    renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                    inputFormat="MMM YYYY"
                  />
                </LocalizationProvider>
              </div>
            ) : null}

            {mergeError ? (
              <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{mergeError}</div>
            ) : null}

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                onClick={() => setMergeModalOpen(false)}
                disabled={mergeRunning}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md border border-blue-500 bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={onMergeActuals}
                disabled={mergeRunning}
              >
                {mergeRunning ? 'Merging...' : 'Merge'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {advancedSettings.open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => (!advancedSettings.saving ? patchAdvancedSettings({ open: false }) : undefined)} />

          <div className="relative z-10 w-full max-w-lg rounded-lg bg-white p-4 shadow-lg">
            <div className="flex items-start justify-between gap-3">
              <div className="text-base font-semibold">RunIt RealTime Advanced Settings</div>
              <button
                type="button"
                className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                onClick={() => patchAdvancedSettings({ open: false })}
                disabled={advancedSettings.saving}
              >
                Close
              </button>
            </div>

            <div className="mt-3 text-sm text-gray-700">
              Configure RunIt SQL credentials and optional custom endpoint URL for this client.
            </div>

            {advancedSettings.loading ? (
              <div className="mt-4 text-sm text-gray-600">Loading settings...</div>
            ) : (
              <div className="mt-4 space-y-3">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={advancedSettings.autoMergeActuals}
                      onChange={(e) => patchAdvancedSettings({ autoMergeActuals: e.target.checked })}
                      disabled={advancedSettings.saving}
                    />
                  }
                  label={<span className="text-sm">Auto merge actuals</span>}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="RunIt SQL User ID"
                  value={advancedSettings.runsqlId}
                  onChange={(e) => patchAdvancedSettings({ runsqlId: e.target.value })}
                  disabled={advancedSettings.saving}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="RunIt SQL Password"
                  type="password"
                  autoComplete="new-password"
                  value={advancedSettings.runsqlPw}
                  onChange={(e) => patchAdvancedSettings({ runsqlPw: e.target.value })}
                  disabled={advancedSettings.saving}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Custom RunIt URL"
                  placeholder="https://..."
                  value={advancedSettings.endpointUrl}
                  onChange={(e) => patchAdvancedSettings({ endpointUrl: e.target.value })}
                  disabled={advancedSettings.saving}
                />
              </div>
            )}

            {advancedSettings.error ? (
              <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                {advancedSettings.error}
              </div>
            ) : null}

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                onClick={() => patchAdvancedSettings({ open: false })}
                disabled={advancedSettings.saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md border border-blue-500 bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={onSaveAdvancedSettings}
                disabled={advancedSettings.loading || advancedSettings.saving}
              >
                {advancedSettings.saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {advancedRun.open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => (!advancedRun.running ? patchAdvancedRun({ open: false }) : undefined)} />

          <div className="relative z-10 w-full max-w-md rounded-lg bg-white p-4 shadow-lg">
            <div className="flex items-start justify-between gap-3">
              <div className="text-base font-semibold">RunIt Advanced Run</div>
              <button
                type="button"
                className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                onClick={() => patchAdvancedRun({ open: false })}
                disabled={advancedRun.running}
              >
                Close
              </button>
            </div>

            <div className="mt-2 text-sm text-gray-700">
              Select which steps to run and optionally merge after. Only selected steps execute.
            </div>

            <div className="mt-4">
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  views={['year', 'month']}
                  label="Period"
                  minDate={moment('2000-01-01')}
                  maxDate={moment()}
                  value={advancedRun.period}
                  onChange={(newValue: Moment | null) => {
                    if (!newValue) return;
                    patchAdvancedRun({ period: newValue });
                  }}
                  disabled={advancedRun.running}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                  inputFormat="MMM YYYY"
                />
              </LocalizationProvider>
            </div>

            <div className="mt-4 space-y-1">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Steps</div>
              <FormControlLabel control={<Checkbox size="small" checked={advancedRun.steps.products} onChange={(e) => patchRunSteps({ products: e.target.checked })} disabled={advancedRun.running} />} label={<span className="text-sm">Products</span>} />
              <FormControlLabel control={<Checkbox size="small" checked={advancedRun.steps.inventory} onChange={(e) => patchRunSteps({ inventory: e.target.checked })} disabled={advancedRun.running} />} label={<span className="text-sm">Inventory</span>} />
              <FormControlLabel control={<Checkbox size="small" checked={advancedRun.steps.orders} onChange={(e) => patchRunSteps({ orders: e.target.checked })} disabled={advancedRun.running} />} label={<span className="text-sm">Orders</span>} />
              <FormControlLabel control={<Checkbox size="small" checked={advancedRun.steps.transfers} onChange={(e) => patchRunSteps({ transfers: e.target.checked })} disabled={advancedRun.running} />} label={<span className="text-sm">Transfers</span>} />
              <FormControlLabel control={<Checkbox size="small" checked={advancedRun.steps.purchaseOrders} onChange={(e) => patchRunSteps({ purchaseOrders: e.target.checked })} disabled={advancedRun.running} />} label={<span className="text-sm">Purchase Orders</span>} />
            </div>

            <div className="mt-4 space-y-1">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Optional Merge</div>
              <FormControlLabel control={<Checkbox size="small" checked={advancedRun.merge.actuals} onChange={(e) => patchRunMerge({ actuals: e.target.checked })} disabled={advancedRun.running || !isCurrentMonth} />} label={<span className="text-sm">Merge actuals <span className="text-xs text-gray-400">(current month only)</span></span>} />
              <FormControlLabel control={<Checkbox size="small" checked={advancedRun.merge.onOrder} onChange={(e) => patchRunMerge({ onOrder: e.target.checked })} disabled={advancedRun.running || !isCurrentMonth} />} label={<span className="text-sm">Merge on-order <span className="text-xs text-gray-400">(current month only)</span></span>} />
            </div>

            {!isCurrentMonth ? (
              <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                Merge options are available only when period is the current month.
              </div>
            ) : null}

            {advancedRun.error ? (
              <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                {advancedRun.error}
              </div>
            ) : null}

            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                onClick={() => patchAdvancedRun({ open: false })}
                disabled={advancedRun.running}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md border border-blue-500 bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={onAdvancedRun}
                disabled={advancedRun.running}
              >
                {advancedRun.running ? 'Running...' : 'Run'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default RunItRealTimeJobSettings;
